// /plan/environments — library page that mirrors the Module-2 AirframesPage
// UX: filter bar by wind-mode, turbulence-mode, source (preset / mine /
// tenant); grid of cards with Edit / Clone to my tenant / Open in
// Workbench / Delete actions. The component reuses PageHeader,
// SpinnerButton, StateChip and the shadcn Select primitives, so the
// visual language stays consistent.

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Filter } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import SpinnerButton from "@/components/SpinnerButton";
import PresetPublishButton from "@/components/PresetPublishButton";
import { toast } from "sonner";
import { sim } from "@/api/endpoints";
import { useAuth } from "@/auth/AuthContext";

function SourceChip({ isPreset }) {
    return (
        <span
            className={`inline-flex items-center border rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${
                isPreset
                    ? "bg-ux-accent/10 text-ux-accent border-ux-accent/30"
                    : "bg-ux-bg-2 text-ux-text-3 border-ux-border-1"
            }`}
        >
            {isPreset ? "preset" : "tenant"}
        </span>
    );
}

function ModePill({ children, tone = "neutral", testId }) {
    const cls =
        tone === "accent"
            ? "text-ux-accent border-ux-accent/30"
            : "text-ux-text-2 border-ux-border-1";
    return (
        <span
            className={`inline-flex items-center border rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-ux-bg-2 ${cls}`}
            data-testid={testId}
        >
            {children}
        </span>
    );
}

export default function EnvironmentsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [filters, setFilters] = useState({ wind_mode: "", turb_mode: "", source: "all" });
    const [cloningId, setCloningId] = useState(null);
    const [openingId, setOpeningId] = useState(null);
    const navigate = useNavigate();
    const { user } = useAuth();

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const scope = filters.source === "mine" ? "mine" : filters.source === "preset" ? "preset" : "tenant";
            const data = await sim.environments.list({ scope, mode: filters.wind_mode || undefined });
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            setErr(e?.message || "Failed to load environments");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    const openWorkbench = async (env) => {
        setOpeningId(env.id);
        try {
            const scn = await sim.scenarios.create({
                name: `Scenario with ${env.name}`,
                description: "Auto-created from /plan/environments",
            });
            await sim.scenarios.attachEnvironment(scn.id, { environment_id: env.id });
            navigate(`/workbench/${scn.id}#section=environment`);
        } catch (e) {
            toast.error(formatApiError(e, "Failed to open in Workbench"));
        } finally {
            setOpeningId(null);
        }
    };

    const cloneToTenant = async (env) => {
        setCloningId(env.id);
        try {
            const cloned = await sim.environments.clone(env.id);
            toast.success(`Cloned "${env.name}" → "${cloned.name}"`);
            await openWorkbench(cloned);
        } catch (e) {
            toast.error(formatApiError(e, "Clone failed"));
        } finally {
            setCloningId(null);
        }
    };

    const visible = items.filter((e) => {
        if (filters.turb_mode && (e.turbulence?.mode || "none") !== filters.turb_mode) return false;
        return true;
    });

    return (
        <div className="space-y-6" data-testid="environments-page">
            <PageHeader
                title="Environments"
                testIdPrefix="environments"
                subtitle="Atmosphere, wind, turbulence, gusts, visibility and solar — the reproducible weather layer of every scenario."
                actions={
                    <div className="flex gap-2">
                        <Link to="/plan/environments/new">
                            <Button data-testid="environments-new" size="sm">
                                <Plus className="w-4 h-4 mr-1" /> New
                            </Button>
                        </Link>
                    </div>
                }
            />

            {/* Filters */}
            <div
                className="flex flex-wrap items-center gap-3 rounded border border-ux-border-1 bg-ux-bg-2/40 p-3"
                data-testid="environments-filters"
            >
                <Filter className="w-4 h-4 text-ux-text-3" aria-hidden="true" />
                <label className="flex items-center gap-2 text-[12px]">
                    <span className="text-ux-text-3 uppercase tracking-wider text-[11px]">Source</span>
                    <Select
                        value={filters.source}
                        onValueChange={(v) => setFilters((f) => ({ ...f, source: v }))}
                    >
                        <SelectTrigger className="w-[140px]" data-testid="filter-source">
                            <SelectValue placeholder="All sources" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All sources</SelectItem>
                            <SelectItem value="mine">Mine</SelectItem>
                            <SelectItem value="preset">Presets</SelectItem>
                        </SelectContent>
                    </Select>
                </label>
                <label className="flex items-center gap-2 text-[12px]">
                    <span className="text-ux-text-3 uppercase tracking-wider text-[11px]">Wind</span>
                    <Select
                        value={filters.wind_mode || "any"}
                        onValueChange={(v) => setFilters((f) => ({ ...f, wind_mode: v === "any" ? "" : v }))}
                    >
                        <SelectTrigger className="w-[140px]" data-testid="filter-wind-mode">
                            <SelectValue placeholder="Any mode" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="constant">Constant</SelectItem>
                            <SelectItem value="log_law">Log-law</SelectItem>
                            <SelectItem value="power_law">Power-law</SelectItem>
                        </SelectContent>
                    </Select>
                </label>
                <label className="flex items-center gap-2 text-[12px]">
                    <span className="text-ux-text-3 uppercase tracking-wider text-[11px]">Turbulence</span>
                    <Select
                        value={filters.turb_mode || "any"}
                        onValueChange={(v) => setFilters((f) => ({ ...f, turb_mode: v === "any" ? "" : v }))}
                    >
                        <SelectTrigger className="w-[140px]" data-testid="filter-turb-mode">
                            <SelectValue placeholder="Any turbulence" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="dryden">Dryden</SelectItem>
                            <SelectItem value="von_karman">von Kármán</SelectItem>
                        </SelectContent>
                    </Select>
                </label>
                <span className="ml-auto text-[11px] text-ux-text-3" data-testid="environments-count">
                    {loading ? "loading…" : `${visible.length} environments`}
                </span>
            </div>

            {err && (
                <div
                    className="rounded border border-ux-danger/30 bg-ux-danger/10 px-3 py-2 text-[12px] text-ux-danger"
                    data-testid="environments-error"
                >
                    {err}
                </div>
            )}

            {!loading && visible.length === 0 ? (
                <EmptyState
                    title="No environments"
                    body="Pick a preset on the left, clone it, and edit in the Workbench."
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {visible.map((env) => (
                        <div
                            key={env.id}
                            className="rounded border border-ux-border-1 bg-ux-bg-2/50 p-4 space-y-3"
                            data-testid={`environment-card-${env.id}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="text-[14px] font-medium text-ux-text-1 truncate">
                                        {env.name}
                                    </div>
                                    <div className="text-[11px] text-ux-text-3 font-mono">
                                        v{env.version || "0.1.0"}
                                    </div>
                                </div>
                                <SourceChip isPreset={!!env.is_preset} />
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                <ModePill tone="accent" testId={`environment-${env.id}-wind-pill`}>
                                    wind · {env.wind?.mode || "constant"}
                                </ModePill>
                                <ModePill testId={`environment-${env.id}-turb-pill`}>
                                    turb · {env.turbulence?.mode || "none"}
                                </ModePill>
                                <ModePill testId={`environment-${env.id}-atmos-pill`}>
                                    atm · {env.atmosphere?.mode || "isa"}
                                </ModePill>
                            </div>
                            {env.notes && (
                                <p className="text-[12px] text-ux-text-2 line-clamp-2">{env.notes}</p>
                            )}
                            <div className="flex flex-wrap gap-2 pt-1">
                                <SpinnerButton
                                    size="sm"
                                    variant="outline"
                                    loading={openingId === env.id}
                                    onClick={() => openWorkbench(env)}
                                    testId={`environment-open-workbench-${env.id}`}
                                >
                                    Open in Workbench
                                </SpinnerButton>
                                {env.is_preset && (
                                    <SpinnerButton
                                        size="sm"
                                        variant="outline"
                                        loading={cloningId === env.id}
                                        onClick={() => cloneToTenant(env)}
                                        testId={`environment-clone-${env.id}`}
                                    >
                                        Clone to my tenant
                                    </SpinnerButton>
                                )}
                                <PresetPublishButton
                                    entityType="environments"
                                    entityId={env.id}
                                    entity={env}
                                    user={user}
                                    size="sm"
                                    variant="outline"
                                    testId={`environment-publish-${env.id}`}
                                    onPublished={load}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
